import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import {
  SERVICE_CATEGORIES,
  SERVICE_CATEGORY_LABELS,
  type ServiceCategory,
  type ServiceSummary,
} from '@shared/types/api';
import {
  useActivateService,
  useAdminServices,
  useDeactivateService,
  useReorderServices,
} from '@/hooks/use-admin';
import { CategoryChip } from './services/category-chip';
import { ServiceEditorDialog } from './services/service-editor-dialog';
import { ServicesTable } from './services/services-table';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

export function AdminServicesPage() {
  const { canCreate, canEdit } = useSectionPermission('SERVICES');
  const { data: services = [], isLoading } = useAdminServices(true);
  const [editing, setEditing] = useState<ServiceSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
  const [filter, setFilter] = useState<ServiceCategory | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const activate = useActivateService();
  const deactivate = useDeactivateService();
  const reorder = useReorderServices();
  const queryClient = useQueryClient();

  const activeCount = useMemo(() => services.filter((s) => s.isActive).length, [services]);
  const inactiveCount = useMemo(() => services.filter((s) => !s.isActive).length, [services]);

  const filtered = useMemo(() => {
    return services.filter((s) => {
      const matchesStatus = statusFilter === 'ACTIVE' ? s.isActive : !s.isActive;
      const matchesCategory = filter === 'ALL' || s.category === filter;
      const q = search.trim().toLowerCase();
      const matchesSearch = !q || (
        s.name.toLowerCase().includes(q) ||
        (s.description && s.description.toLowerCase().includes(q)) ||
        (s.subCategory && s.subCategory.toLowerCase().includes(q)) ||
        (s.associateHsnSacCode && s.associateHsnSacCode.toLowerCase().includes(q))
      );
      return matchesStatus && matchesCategory && matchesSearch;
    });
  }, [services, statusFilter, filter, search]);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const mainItems = [...services];
    const draggedId = filtered[draggedIndex].id;
    const targetId = filtered[index].id;

    const mainDraggedIndex = mainItems.findIndex((s) => s.id === draggedId);
    const mainTargetIndex = mainItems.findIndex((s) => s.id === targetId);

    if (mainDraggedIndex !== -1 && mainTargetIndex !== -1) {
      const [item] = mainItems.splice(mainDraggedIndex, 1);
      mainItems.splice(mainTargetIndex, 0, item);

      queryClient.setQueryData(['admin', 'services', true], mainItems);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = async () => {
    setDraggedIndex(null);
    const ids = services.map((s) => s.id);
    try {
      await reorder.mutateAsync({ ids });
    } catch (err) {
      console.error('Failed to save service order', err);
    }
  };

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Services</h1>
          <p className="text-sm text-gray-600">Catalog of services clients can order.</p>
        </div>
        {canCreate && (
          <Button onClick={() => setCreating(true)}>New service</Button>
        )}
      </div>

      {/* Status Filter Tabs */}
      <div className="mt-4 flex items-center overflow-x-auto no-scrollbar scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-gray-200 gap-2">
        <button
          type="button"
          onClick={() => setStatusFilter('ACTIVE')}
          className={cn(
            'shrink-0 flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
            statusFilter === 'ACTIVE'
              ? 'border-brand-600 text-brand-800'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
          )}
        >
          <span>Active Services</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              statusFilter === 'ACTIVE'
                ? 'bg-brand-100 text-brand-800'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {activeCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('INACTIVE')}
          className={cn(
            'shrink-0 flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap',
            statusFilter === 'INACTIVE'
              ? 'border-red-600 text-red-700'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
          )}
        >
          <span>Deactivated Services</span>
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-semibold',
              statusFilter === 'INACTIVE'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-600',
            )}
          >
            {inactiveCount}
          </span>
        </button>
      </div>

      {/* Search Bar */}
      <div className="mt-4">
        <div className="relative max-w-md">
          <svg
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search service name, category, description, HSN..."
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        <CategoryChip label="All" active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
        {SERVICE_CATEGORIES.map((cat) => (
          <CategoryChip
            key={cat}
            label={SERVICE_CATEGORY_LABELS[cat]}
            active={filter === cat}
            onClick={() => setFilter(cat)}
          />
        ))}
      </div>

      <ServicesTable
        services={filtered}
        isLoading={isLoading}
        statusFilter={statusFilter}
        draggedIndex={draggedIndex}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onEdit={setEditing}
        onActivate={(id) => activate.mutate(id)}
        isActivating={activate.isPending}
        onDeactivate={(id) => deactivate.mutate(id)}
        isDeactivating={deactivate.isPending}
      />

      {creating && canCreate ? (
        <ServiceEditorDialog existingServices={services} onClose={() => setCreating(false)} />
      ) : null}
      {editing ? (
        <ServiceEditorDialog
          existing={editing}
          existingServices={services}
          readOnly={!canEdit}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
